import { RootState } from '@next/core-lib/app';
import { SettingsDataObject } from '@next/core-lib/settings';
import { EntityState } from '@ngrx/entity';
import { Product } from 'src/app/shared/models/shared.model';

import {
  DosatorConversions,
  DosatorProduct,
  EnergySystem,
  LysineSystem,
} from '../../settings/models/settings.model';

export interface FeedAdviceModuleState extends RootState {
  feedadvice: FeedAdviceState;
  products?: Product[];
  customerProducts?: Product[];
  feedAdvices?: FeedAdvice[];
  feedAdvicesFromReference?: FeedAdvice[];
  selectedFeedAdvice?: FeedAdvice;
  selectedFeedadviceTypes?: string[];
  locations?: number[];
  isLoaded: boolean;
  showLysine?: boolean;
  showEnergy?: boolean;
  showDosator?: boolean;
  selectedCountry?: string;
  selectedLocations?: string[];
  feedAdviceType: FeedAdviceType;
}

export type FeedAdviceState = EntityState<FeedAdvice>;

export interface FeedAdvice {
  id: number;
  advisorProfileId?: number;
  advisorName?: string;
  customerId?: number;
  description: string;
  type: string;
  feedAdviceType: FeedAdviceType;
  phaseTransition?: PhaseTransitionTypes;
  feedIntake?: string;
  name: string;
  isBarrow?: boolean;
  isBoar?: boolean;
  isGilt?: boolean;
  isSow?: boolean;
  locationIds?: number[];
  numberOfPhases?: number;
  phases: Phase[];
  updated: Date;
  fatteningRearingFeedAdviceLines: FeedAdviceLine[];
  energySystem: EnergySystem;
  lysineSystem: LysineSystem;
  terminalBoar?: TerminalBoar | string;
  country?: string;
  isReferenceFeedAdvice: boolean;
  usedReferenceFeedAdviceId?: number;
  geneticLine?: string;
  parity?: ParityType;
  useParitySystem?: boolean;
  parityAlternative2?: string;
  parityAlternative3?: string;
  parityAlternative4?: string;
  parityAlternative5?: string;
  parityAlternative6?: string;
  bodyCondition?: BodyConditionType;
  pigletsPerFarrowingCategory?: number;
  transitionDay?: number;
  inputSystem: InputSystemType;
  sowFeedAdviceLines: SowFeedAdviceLine[];
  weanedPigletFeedAdviceLines: FeedAdviceLine[];
  weaningAge?: number;
  weaningWeight?: number;
  rearingAge?: number;
  finalWeight?: number;
  showDosatorColumns?: boolean;
  selectedDosatorSettingsId?: number;
  feedingType?: FeedingType;
}

export interface TableScheme {
  numberOfPhases: number;
  phases: Phase[];
  phaseTransition: PhaseTransitionTypes;
  feedAdviceType: FeedAdviceType | string;
  feedAdviceLines: FeedAdviceLine[];
  inputSystem: InputSystemType;
  pigletWeaningAge?: number;
  rearingAge?: number;
  usedReferenceFeedAdviceId?: number;
}

export interface Phase extends SettingsDataObject {
  phaseNumber: number;
  product?: Product;
}

export interface ProductTotals {
  phaseNumber?: number;
  productCode: string;
  productName: string;
  ewKg: number;
  totalKg: number;
  totalNettoPrice: number;
  nettoPricePer100Kg: number;
}

export interface ProductTotalsSows {
  productCode: string;
  productName: string;
  unitNettoPrice?: number;
  parityTotals?: ParityTotals[];
}

export interface ParityTotals {
  parity: number;
  feedKg: number;
}

export interface FeedAdviceLine {
  week?: number;
  day?: number;
  weekAge?: number;
  age?: number;
  weight: number;
  correctionFactor?: number;
  energyValuePerDay: number;
  intestinalDigestibleLysinePerDay: number;
  cumulativeFoodInKg: number;
  foodPerDay: number;
  consumptions: Consumption[];
  referenceSchemeIntestinalDigestibleLysinePerDay?: number;
  referenceSchemeEnergyValuePerDay?: number;
  referenceSchemeCumulativeFoodInKg?: number;
  feedingTimesPerDay?: number;
}

export interface SowFeedAdviceLine {
  id?: number;
  dayFrom: number;
  dayTo: number;
  parityRegistrations: ParityRegistration[];
  product: Product;
  feedingTimesPerDay?: number;
}

export interface Consumption {
  phaseNumber: number;
  productIntakeInPercentage?: number;
  dosatorPosition?: number;
}

export interface SchemeTableRow extends FeedAdviceLine {
  line: number;
  cumulative: number;
}

export interface ValidationError {
  line: number;
  type: string;
}

export interface FeedAdviceProperties {
  phaseTransition?: PhaseTransitionTypes;
  terminalBoar?: string;
  feedAdviceType: FeedAdviceType;
  genders?: string[];
  feedintake?: FeedIntakeTypes;
  phases?: number;
  geneticLine?: GeneticLineType;
  bodyCondition?: BodyConditionType;
  parityCategory?: ParityType;
  inputSystem?: InputSystemType;
  transitionDay?: number;
  pigletsPerFarrowingCategory?: number;
  weaningWeight?: number;
  weaningAge?: number;
  rearingAge?: number;
  showLysine?: boolean;
  showEnergy?: boolean;
  showDosator?: boolean;
  selectedDosatorSettingsId?: number;
  feedingType?: FeedingType;
}

export enum TerminalBoar {
  DUROC = 'Duroc',
  PIETRAIN = 'Piètrain',
  TEMPO = 'Tempo',
}

export enum FeedIntakeTypes {
  LOW = 'Low',
  AVERAGE = 'Average',
  HIGH = 'High',
}

export enum GenderTypes {
  BARROW = 'barrow',
  BOAR = 'boar',
  GILT = 'gilt',
  SOW = 'sow',
}

export enum FeedAdviceType {
  FATTENING = 'Fattening',
  REARING = 'Rearing',
  SOWS_BREEDTOFARROW = 'SowsBreedToFarrow',
  SOWS_FARROWTOWEAN = 'SowsFarrowToWean',
  SOWS_WEANTOBREED = 'SowsWeanToBreed',
  WEANED_PIGLETS = 'WeanedPiglet',
}

export enum PhaseTransitionTypes {
  GRADUAL = 'Gradual',
  ABRUPT = 'Abrupt',
}

export enum ParityType {
  ONETOTWO = 'OneToTwo',
  TWOTOFOUR = 'TwoToFour',
  ONETOFOUR = 'OneToFour',
  ONETOFIVE = 'OneToFive',
}

export enum BodyConditionType {
  VERYLEAN = 'VeryLean',
  LEAN = 'Lean',
  IDEAL = 'Ideal',
  FAT = 'Fat',
}

export enum GeneticLineType {
  DANBRED = 'Danbred',
  HYPOR = 'Hypor',
  PIC = 'PIC',
  TOPIGS20 = 'Topigs 20',
  TOPIGS50 = 'Topigs 50',
  TN60 = 'TN60',
  TN70 = 'TN70',
  ROTATIONCROSS = 'Rotation cross',
}

export enum InputSystemType {
  ENERGYDAY = 'EnergyDay',
  KGDAY = 'KgDay',
  DOSATOR = 'Dosator',
}

export enum FeedingType {
  MANUAL = 'Manual',
  CDI = 'CDI',
}

export interface ParityRegistration {
  parity: number;
  ewKg: number;
  feedKg: number;
  lysKg: number;
  dosatorPosition?: number;
}

export interface FeedAdviceTypeMenuItem {
  name: FeedAdviceType;
  period: string;
  type: string;
}

export interface FeedAdviceDataSource {
  id: number;
  name: string;
  locations: string;
  locationIds: number[];
  date: Date;
  advisor: string;
  feedAdviceType: FeedAdviceType;
  type: string;
  genders: string;
  feedintake: string;
  nrOfPhases: number;
  phaseTransition: string;
  description: string;
}

export interface SowFeedadviceTableRow {
  id: number;
  dayFrom: number;
  dayTo: number;
  energyday_input_1: number;
  energyday_input_2: number;
  energyday_input_3: number;
  energyday_input_4: number;
  energyday_input_5: number;
  energyday_readonly_1: number;
  energyday_readonly_2: number;
  energyday_readonly_3: number;
  energyday_readonly_4: number;
  energyday_readonly_5: number;
  product: Product;
  kgday_input_1: number;
  kgday_input_2: number;
  kgday_input_3: number;
  kgday_input_4: number;
  kgday_input_5: number;
  kgday_readonly_1: number;
  kgday_readonly_2: number;
  kgday_readonly_3: number;
  kgday_readonly_4: number;
  kgday_readonly_5: number;
  lysine_readonly_1: number;
  lysine_readonly_2: number;
  lysine_readonly_3: number;
  lysine_readonly_4: number;
  lysine_readonly_5: number;
  feedingTimesPerDay: number;
  dosator_input_1: number;
  dosator_input_2: number;
  dosator_input_3: number;
  dosator_input_4: number;
  dosator_input_5: number;
  dosator_readonly_input_1: number;
  dosator_readonly_input_2: number;
  dosator_readonly_input_3: number;
  dosator_readonly_input_4: number;
  dosator_readonly_input_5: number;
}

export interface LocationItem {
  value: number;
  name: string;
  selected: boolean;
}

export interface DosatorSettingsConversionLimits {
  lowerConversion?: DosatorConversions;
  higherConversion?: DosatorConversions;
}

export enum CalculationDirection {
  FromLitersToKg,
  FromKgToLiters,
}

export interface CalculationResult {
  validationMessage?: string;
}

export interface CalculationConsumptionResult extends CalculationResult {
  consumption?: Consumption;
}

export interface CalculationPhaseResult extends CalculationResult {
  phase?: Phase;
}

export interface CalculationDosatorSettingsResult extends CalculationResult {
  dosatorSettings?: DosatorProduct;
}
